import { INotification } from '@magicbell/react-headless';
import { PaletteColor, Theme } from '@mui/material';
import Iconify from 'src/components/iconify';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  CodeBracketIcon,
  UserMinusIcon,
  UserPlusIcon,
} from '@heroicons/react/24/solid';
import { getSolidIconByOutreachType } from 'src/components/outreach-type-avatar/utils';
import { QueryClient } from '@tanstack/react-query';

export function getNotificationIcon(notificationType: string | null) {
  switch (notificationType) {
    case 'data_import':
      return <Iconify icon="eva:cloud-upload-fill" width={24} height={24} />;
    case 'list_export':
      return <Iconify icon="eva:cloud-download-fill" width={24} height={24} />;
    case 'model_run':
      return <ArrowPathIcon width={24} height={24} />;
    case 'model_import':
      return <Iconify icon="eva:cloud-upload-fill" width={24} height={24} />;
    case 'dashboard_refresh':
      return <ArrowPathIcon width={24} height={24} />;
    case 'tags_import':
      return <Iconify icon="eva:pricetags-fill" width={24} height={24} />;
    case 'mv_creation':
      return <CodeBracketIcon width={24} height={24} />;
    case 'project_canvass':
      return getSolidIconByOutreachType('canvass', 24);
    case 'project_mail':
      return getSolidIconByOutreachType('mail', 24);
    case 'project_call':
      return getSolidIconByOutreachType('call', 24);
    case 'project_text':
      return getSolidIconByOutreachType('text', 24);
    case 'project_text_survey':
      return getSolidIconByOutreachType('text-survey', 24);
    case 'project_ivr':
      return getSolidIconByOutreachType('ivr', 24);
    case 'org_approval_request':
      return <CheckBadgeIcon width={24} height={24} />;
    case 'user_interaction':
      return getSolidIconByOutreachType('user', 24);
    case 'user_interaction_invited':
      return <UserPlusIcon width={24} height={24} />;
    case 'user_interaction_removed':
      return <UserMinusIcon width={24} height={24} />;
    default:
      return <ChatBubbleLeftRightIcon width={24} height={24} />;
  }
}

export function extractUrlFromHtml(htmlString: string): string | null {
  // Use DOMParser to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Attempt to find the first <a> tag and extract its href attribute
  const anchor = doc.querySelector('a');

  return anchor ? anchor.getAttribute('href') : null;
}

export function handleQueryClientInvalidation(
  notification: ProcessedNotification,
  queryClient: QueryClient
) {
  const title = (notification.title || '').toLowerCase();
  if (title.includes('model run complete') || title.includes('model run failed')) {
    queryClient.invalidateQueries({ queryKey: ['models'] });
    queryClient.invalidateQueries({ queryKey: ['tag-models'] });
    queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
    queryClient.invalidateQueries({ queryKey: ['filterCategories'] });
  }
}

export function processNotification(
  theme: Theme,
  notification: INotification
): ProcessedNotification {
  const { title, customAttributes } = notification;
  let { content } = notification;
  const notificationType = (customAttributes as Record<string, string | undefined>)?.type as
    | string
    | undefined;

  const actionText = (customAttributes as Record<string, string | undefined>)?.actionText as
    | string
    | undefined;

  if (notification.content?.includes('ValueError')) {
    content =
      'Please use Intercom or Contact <a href="mailto:support@numinar.com">Support@numinar.com</a> for Help';
  } else if (
    notification.content?.includes('https://') &&
    !notification.content?.includes('<a href')
  ) {
    const urlRegex = /(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;
    content = content?.replace(urlRegex, '<a href="$1">Click here to download</a>') || '';
  }

  if (!notification.category || !notificationType) {
    const title = notification.title.toLowerCase();
    const content = (notification.content || '').toLowerCase();
    if (content.includes('your uploaded data has been processed successfully')) {
      return {
        title,
        content,
        icon: getNotificationIcon('data_import'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
      };
    } else if (title.includes('an error occurred while processing your upload')) {
      return {
        title,
        content,
        icon: getNotificationIcon('list_export'),
        color: 'error',
        palette: theme.palette.error,
        variant: 'error',
        actionText: 'Contact Support',
        actionUrl: 'mailto:support@numinar.com',
      };
    } else if (title.includes('your download is ready for')) {
      const url = extractUrlFromHtml(notification.content || '') || '';
      const exportId = extractExportIdFromUrl(url);
      return {
        title,
        content,
        icon: getNotificationIcon('list_export'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
        actionText: 'Download Export',
        actionUrl: exportId ? undefined : url,
        exportId: exportId || undefined,
      };
    } else if (title.includes('an error occurred while exporting')) {
      return {
        title,
        content,
        icon: getNotificationIcon('list_export'),
        color: 'error',
        variant: 'error',
        palette: theme.palette.error,
        actionText: 'Contact Support',
        actionUrl: 'mailto:support@numinar.com',
      };
    } else if (title.includes('model run complete')) {
      return {
        title,
        content,
        icon: getNotificationIcon('model_run'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
      };
    } else if (title.includes('model run failed')) {
      return {
        title,
        content,
        icon: getNotificationIcon('model_run'),
        color: 'error',
        variant: 'error',
        palette: theme.palette.error,
        actionText: 'Contact Support',
        actionUrl: 'mailto:support@numinar.com',
      };
    } else if (title.includes('model import complete')) {
      return {
        title,
        content,
        icon: getNotificationIcon('model_import'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
      };
    } else if (title.includes('model import failed')) {
      return {
        title,
        content,
        icon: getNotificationIcon('model_import'),
        color: 'error',
        variant: 'error',
        palette: theme.palette.error,
      };
    } else if (title.includes('dashboard refresh complete')) {
      return {
        title,
        content,
        icon: getNotificationIcon('dashboard_refresh'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
      };
    } else if (
      title.includes('auto data import complete for') ||
      title.includes('auto abev import complete for')
    ) {
      return {
        title,
        content,
        icon: getNotificationIcon('data_import'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
      };
    } else if (
      title.includes('auto data import failed') ||
      title.includes('auto abev import failed')
    ) {
      return {
        title,
        content,
        icon: getNotificationIcon('data_import'),
        color: 'error',
        variant: 'error',
        palette: theme.palette.error,
      };
    } else if (title.includes('assign tags to your uploaded file')) {
      return {
        title,
        content,
        icon: getNotificationIcon('tags_import'),
        color: 'error',
        variant: 'error',
        palette: theme.palette.error,
        actionUrl: extractUrlFromHtml(notification.content || '') || undefined,
        actionText: 'See Details',
      };
    } else if (title.includes('your mail project')) {
      return {
        title,
        content,
        icon: getNotificationIcon('project_mail'),
        color: 'success',
        variant: 'success',
        palette: theme.palette.success,
      };
    }
  }

  const { palette, color, variant } = convertNotificationTypeToVariant(
    theme,
    notificationType || 'primary'
  );
  return {
    title,
    content,
    icon: getNotificationIcon(notification.category),
    color: color,
    variant: variant,
    palette: palette,
    actionUrl: notification.actionUrl || '',
    actionText: actionText,
  };
}
export function convertNotificationTypeToVariant(
  theme: Theme,
  notificationType: string
): NotificationTypeVariant {
  switch (notificationType) {
    case 'primary':
      return { palette: theme.palette.primary, color: 'primary', variant: 'default' };
    case 'success':
      return { palette: theme.palette.success, color: 'success', variant: 'success' };
    case 'error':
      return { palette: theme.palette.error, color: 'error', variant: 'error' };
    case 'info':
      return { palette: theme.palette.info, color: 'info', variant: 'info' };
    case 'warning':
      return { palette: theme.palette.warning, color: 'warning', variant: 'warning' };
    default:
      return { palette: theme.palette.primary, color: 'primary', variant: 'default' };
  }
}
export type NotificationTypeVariant = {
  palette: PaletteColor;
  color: 'primary' | 'success' | 'error' | 'info' | 'warning';
  variant: 'default' | 'error' | 'success' | 'warning' | 'info';
};
export type ProcessedNotification = {
  title: string | null;
  content: string | null;
  icon: React.JSX.Element;
  color: 'primary' | 'success' | 'error' | 'info' | 'warning';
  palette: PaletteColor;
  variant: 'default' | 'error' | 'success' | 'warning' | 'info';
  actionText?: string;
  actionUrl?: string;
  exportId?: string;
};

export function extractExportIdFromUrl(url: string): string | null {
  const match = url.match(/\/exports\/([a-f0-9-]+)\/download/i);
  return match ? match[1] : null;
}
